import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../common/Table";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  PlaylistTag,
  PlaylistTagFormData,
  PlaylistTagUpdateFormData,
  PlaylistTagRequest,
} from "../../schemas/playlistTagSchema";
import { usePlaylistTagsInfinite } from "../../hooks/usePlaylistTagsInfinite";
import PlaylistTagForm from "../forms/PlaylistTagForm";

const PlaylistTags: React.FC = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<PlaylistTag | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");

  const {
    playlistTags,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createPlaylistTag,
    updatePlaylistTag,
    deletePlaylistTags,
  } = usePlaylistTagsInfinite({ search });

  const handleEdit = (tag: PlaylistTag) => {
    setSelectedTag(tag);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (tag: PlaylistTag) => {
    if (window.confirm(`Delete Playlist Tag "${tag.name}"?`)) {
      await deletePlaylistTags([tag.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (window.confirm(`Delete ${ids.length} selected playlist tag(s)?`)) {
      deletePlaylistTags(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = async (
    data: PlaylistTagFormData | PlaylistTagUpdateFormData,
  ) => {
    setIsSubmitting(true);
    try {
      if (selectedTag?.id) {
        await updatePlaylistTag({
          id: selectedTag.id,
          playlistTag: data as PlaylistTagUpdateFormData,
        });
      } else {
        const payload: PlaylistTagRequest = {
          name: (data as PlaylistTagFormData).name,
          description: (data as PlaylistTagFormData).description,
        };
        await createPlaylistTag(payload);
      }
    } finally {
      setSelectedTag(undefined);
      setIsFormModalOpen(false);
      setIsSubmitting(false);
    }
  };

  const columns: ColumnDef<PlaylistTag>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "description", header: "Description" },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl font-bold text-gray-800">Playlist Tags</h1>
        <button
          onClick={() => {
            setIsFormModalOpen(true);
          }}
          className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      <Table
        columns={columns}
        data={playlistTags as PlaylistTag[]}
        actions={actions}
        isLoading={isLoading}
        maxHeight="48vh"
        pagination
        searchable
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onSearch={setSearch}
        selectable={true}
        getRowId={(tag) => tag.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        bulkActions={[
          {
            label: "Delete Selected",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: handleBulkDelete,
            variant: "danger" as const,
          },
        ]}
      />

      {isFormModalOpen && (
        <PlaylistTagForm
          isOpen={isFormModalOpen}
          onCancel={() => {
            setIsFormModalOpen(false);
            setSelectedTag(undefined);
          }}
          initialData={selectedTag}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          isUpdate={!!selectedTag}
        />
      )}
    </>
  );
};

export default PlaylistTags;
